import * as _ from "lodash";
import * as Redux from "redux";
import { ThunkAction } from "redux-thunk";
import { fetchAgentGroups } from "../../shared/actions/AgentGroupActions";
import { AgentGroup, AgentVersionInfo, defaultAgentGroupName } from "../../shared/model/AgentGroup";
import { HashMap } from "../../shared/model/HashMap";
import { ThunkResult, Utilities } from "../../shared/utilities";

import { AgentStatusPerAgentAndProcess } from "../model/AgentStatusPerAgentAndProcess";
import * as AppPath from "../model/AppPath";

import { Permissions } from "../../shared/model/AllPermissions";
import { FilterCategory, SortByExeutionColumns } from "../model/ListState";
import { Draft, Process, ProcessListResponse } from "../model/Process";
import { ProcessCountState } from "../model/ProcessCountState";
import { ProcessVersion, ProcessVersionDetails } from "../model/ProcessVersion";
import { getAgentGroup, getCurrentAgentGroup, State } from "../model/State";
import { SubprocessUsageModel } from "../model/SubprocessUsageModel";
import { getVisibleColumnsFromStore } from "../model/VisibleColumnsStore";

import { CHANGE_AGENT_GROUP, ChangeAgentGroupAction } from "./ProcessListActions";
import { fetchTasks } from "./SearchActions";
import { resetProcessCounts } from "./SignalRActions";
import { getRowsPerPage } from "processList/model/RowsPerPage";
import { getAggregateCountsForCurrentAgentGroup, getExecutionFilter } from "processList/model/ProcessCountSelectors";
import { ProcessAggregateCount } from "processList/model/ProcessAggregateCount";
import { ProcessColumnIdentifier } from "processList/model/ProcessList";
import Time from "shared/Time";
import { LogLevelUsageData, LogRetentionUsageData } from "processList/model/LogLevelAndRetentionUsageModel";
import { IProcessVariable } from "../../processEditor/model/IProcessData";

/** The fetch path used, differs for processes and subprocesses */
export const getFetchPath = () => AppPath.isSubprocessList() ? "subprocessList" : "processList";

export function fetchInitialData(agentGroupEnvironment: AppPath.AgentGroupEnvironment | undefined, loadProcessList: boolean): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        try {
            dispatch(fetchStart(FetchId.all));
            const searchParams = AppPath.getSearchParameters();
            const state = getState();
            const countsTimeLimitDays = state.listState.countsTimeLimitDays;

            let fetchProcessCountsPromise: Promise<ProcessCountsResult> | boolean = false;
            let fetchProcessDataPromise: Promise<ProcessListResponse> | boolean = false;

            if (agentGroupEnvironment?.environmentName && agentGroupEnvironment.agentGroupName && loadProcessList) {
                dispatch(fetchEnvironmentVariables());

                // if the list sorted by execution counts, fetch them first 
                // because the guids from the fetch result are used for fetching processes
                if (searchParams.sortBy === SortByExeutionColumns.executionsPerDay) {
                    fetchProcessCountsPromise = fetchProcessCountData(agentGroupEnvironment.environmentName, agentGroupEnvironment.agentGroupName, countsTimeLimitDays);
                    const countData = await fetchProcessCountsPromise;
                    dispatch(resetProcessCounts(countData.executionCounts));
                }
                
                // all parameters given -> start fetching processes with them,
                // so the page view doesn't need to wait so long
                fetchProcessDataPromise = fetchProcessData(
                    agentGroupEnvironment.environmentName,
                    agentGroupEnvironment.agentGroupName,
                    getState());

            }

            dispatch(fetchStart(FetchId.agentGroups));
            const agentGroupHash = await fetchAgentGroups();
            dispatch(receiveAgentGroups(agentGroupHash));
            dispatch(fetchFinish(FetchId.agentGroups));

            let initialAgentGroup: AgentGroup | null | undefined = null;
            if (agentGroupEnvironment?.agentGroupName) {
                initialAgentGroup = agentGroupHash[agentGroupEnvironment.agentGroupName];
                if (!initialAgentGroup) {
                    // Do a case insensitive search for the agent group name
                    const agentGroupKey = Object.keys(agentGroupHash).find(n => n.toLowerCase() === agentGroupEnvironment.agentGroupName?.toLowerCase());
                    if (agentGroupKey) {
                        initialAgentGroup = agentGroupHash[agentGroupKey];
                    }
                }
            } else {
                // No agent group passed. Select first agent group by that environment
                initialAgentGroup = _.find(agentGroupHash, (x) => x.environment === agentGroupEnvironment?.environmentName);
            }

            if (!initialAgentGroup) {
                throw Error(`Agent group '${agentGroupEnvironment?.agentGroupName || agentGroupEnvironment?.environmentName}' not found.`);
            }

            // The url and the agent group that will be fetched do not match.
            // This happens when process list is accessed with only the environmentName in the url
            if ((!fetchProcessDataPromise && initialAgentGroup.name !== agentGroupEnvironment?.agentGroupName)) {
                dispatch<ChangeAgentGroupAction>({
                    type: CHANGE_AGENT_GROUP,
                    agentGroupName: initialAgentGroup.name
                });

                const selectedAgentGroup = getAgentGroup(getState(), initialAgentGroup.name);
                AppPath.setAgentGroupToPath(selectedAgentGroup.environment, selectedAgentGroup.name);
                if (loadProcessList) {
                    // if the list sorted by execution counts, fetch them first 
                    // because the guids from the fetch result are used for fetching processes
                    if (searchParams.sortBy === SortByExeutionColumns.executionsPerDay) {
                        fetchProcessCountsPromise = fetchProcessCountData(selectedAgentGroup.environment, selectedAgentGroup.name, countsTimeLimitDays);
                        const countData = await fetchProcessCountsPromise;
                        dispatch(resetProcessCounts(countData.executionCounts));
                    }

                    fetchProcessDataPromise = fetchProcessData(
                        initialAgentGroup.environment,
                        initialAgentGroup.name,
                        getState());
                }
            }

            const fetchAgentStatusesPromise = fetchAgentStatuses(initialAgentGroup.environment, initialAgentGroup.name);

            if (!fetchProcessCountsPromise) {
                fetchProcessCountsPromise = fetchProcessCountData(initialAgentGroup.environment, initialAgentGroup.name, countsTimeLimitDays);
            }

            dispatch(fetchDrafts());
            // We don't fetch processes if instance list is opened directly
            if (fetchProcessDataPromise) {
                // if sort of search is on count we need those before we fetch processes
                // wait for process data first, so the list never flashes "no processes"
                dispatch(fetchStart(FetchId.processes))

                const countData = await fetchProcessCountsPromise;
                dispatch(resetProcessCounts(countData.executionCounts));

                const processData = await fetchProcessDataPromise;
                dispatch(receiveProcesses(processData));
                dispatch(fetchFinish(FetchId.processes));
            }

            if (searchParams.filterCategory === FilterCategory.Tasks) {
                dispatch(fetchTasks());
            }

            if (shouldFetchSubprocessUsageData(searchParams.filterCategory as FilterCategory)) {
                dispatch(fetchSubprocessUsageData(initialAgentGroup.environment, initialAgentGroup.name));
            }

            if (shouldFetchLogLevelUsageData()) {
                dispatch(fetchLogLevelUsageData(initialAgentGroup.environment, initialAgentGroup.name));
            }
    
            if (shouldFetchLogRetentionUsageData()) {
                dispatch(fetchLogRetentionUsageData(initialAgentGroup.environment, initialAgentGroup.name));
            }

            dispatch(fetchStart(FetchId.agentStatusesAndProcessCounts));
            const [agentStatuses, processCounts] = await Promise.all([
                fetchAgentStatusesPromise,
                fetchProcessCountsPromise]);
            dispatch(receiveAgentStatuses(agentStatuses));
            dispatch(resetProcessCounts(processCounts.executionCounts));
            dispatch(fetchFinish(FetchId.agentStatusesAndProcessCounts));
            dispatch(fetchAvailableTags());

            dispatch(fetchFinish(FetchId.all));
        } catch (err) {
            dispatch(reportFetchError(err));
        }
    };
}

function fetchProcessData(environmentName: string,
    agentGroupName: string, state: State): Promise<ProcessListResponse> {
    const searchParams = AppPath.getSearchParameters();
    const search: { [key: string]: string | null } = {
        pageNumber: (searchParams.page ?? 1).toString(),
        pageSize: (searchParams.pageSize ?? getRowsPerPage()).toString(),
        descending: searchParams.desc != undefined ? String(searchParams.desc) : "false",
        sortBy: searchParams.sortBy?.toString() as string ?? null,
        filterBy: (searchParams.filterCategory ?? FilterCategory.Text).toString(),
        filterText: searchParams.filterText as string ?? null,
        highlightProcess: searchParams.highlightProcess?.toString() ?? null
    }

    if (Object.values(SortByExeutionColumns).includes(searchParams.sortBy as SortByExeutionColumns)) {
        setSearchParamsForSortByExecutionColumn(searchParams, state, search);
    }
    if (searchParams.sortBy as string == ProcessColumnIdentifier.usedByProcessesList) {
        setSearchParamsForSortByUsedByProcesses(state, search);
    }
    if (searchParams.filterCategory == FilterCategory.ExecutionsSince || searchParams.filterCategory == FilterCategory.ErrorsSince && searchParams.filterText) {
        setSearchParamsForExecutionCountFilter(searchParams, state, search);
    }
    return Utilities.getFetch(`api/${getFetchPath()}/${environmentName}/${agentGroupName}`, search);
}

function setSearchParamsForSortByExecutionColumn(searchParams: AppPath.ProcessListSearchParams & AppPath.InstanceListSearchParameters, state: State, search: { [key: string]: string | null; }) {
    const counts = getAggregateCountsForCurrentAgentGroup(state, getExecutionFilter(searchParams.filterCategory, searchParams.filterText as string))
    let countArray = Object.keys(counts).map(k => { return { count: counts[k], guid: k }; }).filter((x) => x.count.latestExecutionStartTime != null);
    switch (search.sortBy) {
        case SortByExeutionColumns.failed.toString():
            countArray.sort((x, c) => c.count.failed - x.count.failed);
            break;
        case SortByExeutionColumns.running.toString():
            countArray.sort((x, c) => c.count.running - x.count.running);
            break;
        case SortByExeutionColumns.successful.toString():
            countArray.sort((x, c) => c.count.successful - x.count.successful);
            break;
        case SortByExeutionColumns.executionsPerDay.toString():
            // sort by current day executions
            countArray.sort((x, c) => c.count.executionsPerDay[c.count.executionsPerDay.length - 1] - x.count.executionsPerDay[x.count.executionsPerDay.length - 1]);
            break;
        case SortByExeutionColumns.erroredNotAcknowledged.toString():
            countArray.sort((x, c) => c.count.erroredNotAcknowledged - x.count.erroredNotAcknowledged);
            break;
        case SortByExeutionColumns.latestExecutionStartTime.toString():
            countArray.sort((x, c) => Date.parse(c.count.latestExecutionStartTime as string) - Date.parse(x.count.latestExecutionStartTime as string));
            break;
    }

    countArray = countArray.slice(0, searchParams.pageSize ?? getRowsPerPage());
    search.sortByGuids = `[${countArray.map(x => `"${x.guid}"`).join(",")}]`;
    // When sorting by ID we can only show one page
    search.pageNumber = "1";
}

function setSearchParamsForSortByUsedByProcesses(state: State, search: { [key: string]: string | null; }) {
    search.sortByGuids = `[${Object.keys(state.usedInProcessesMap).map(x => `"${x}"`).join(",")}]`;
    // When sorting by ID we can only show one page
    search.pageNumber = "1";
}

// change filterText to list of guids that should be fetched
function setSearchParamsForExecutionCountFilter(searchParams: AppPath.ProcessListSearchParams & AppPath.InstanceListSearchParameters, state: State, search: { [key: string]: string | null; }) {
    const latestTimestamp = getFilterTimestamp(searchParams.filterText as string);
    if (!latestTimestamp) {
        return;
    }
    const counts = getAggregateCountsForCurrentAgentGroup(state, getExecutionFilter(searchParams.filterCategory, searchParams.filterText as string));
    const countArray = Object.keys(counts).map(k => { return { count: counts[k], guid: k }; });

    const filtered = getFilteredCounts(countArray, latestTimestamp, searchParams.filterCategory as string);

    filtered.sort((x, c) => c.count.total - x.count.total);
    const selected = paginate(filtered, searchParams.pageSize ?? getRowsPerPage(), searchParams.page ?? 1);
    search.filterText = `[${selected.map(x => `"${x.guid}"`).join(",")}]`;

    // Pagenumber is always one for backend
    search.pageNumber = "1";
    // totalcount needs to be spoofed and given to backend to return back to frontend
    search.totalCount = filtered.length.toString();
}

function getFilteredCounts(countArray: { count: ProcessAggregateCount; guid: string; }[], latestTimestamp: string, filterCategory: string) {

    if (filterCategory == FilterCategory.ErrorsSince) {

        return countArray.filter((x) => x.count.latestFailed && x.count.latestFailed > latestTimestamp);
    }
    return countArray.filter((x) => x.count.latestFailed && x.count.latestFailed > latestTimestamp
        || (x.count.latestRunning && x.count.latestRunning > latestTimestamp)
        || (x.count.latestSuccessful && x.count.latestSuccessful > latestTimestamp));
}

function paginate(array: Array<{ count: ProcessAggregateCount, guid: string }>, page_size: number, page_number: number): Array<{ count: ProcessAggregateCount, guid: string }> {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
}

const getFilterTimestamp = (filterDays: string) => {
    if (!filterDays) {
        return "";
    }
    const days = parseInt(filterDays as string, 10);
    if (isNaN(days)) {
        return "";
    }
    return Time.formatISO(Time.add(new Date(), { days: -days }).toISOString(), Time.dateFormat);
};

export function fetchDrafts(): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            if (state.currentAgentGroupName !== defaultAgentGroupName ||
                !state.userPermissions[Permissions.ProcessEdit]) {
                // not in default or not allowed -> clear
                return dispatch(receiveDrafts([]));
            }

            dispatch(fetchStart(FetchId.drafts));
            const drafts = await Utilities.getFetch<Draft[]>(`api/${getFetchPath()}/drafts`);
            dispatch(receiveDrafts(drafts));
            dispatch(fetchFinish(FetchId.drafts));
        }
        catch (err) {
            dispatch(reportFetchError(err))
        }
    };
}

export function fetchSubprocessUsageData(environmentName: string, agentGroupName: string): ThunkResult<void, State> {
    return async (dispatch) => {
        dispatch(fetchStart(FetchId.extraProcessData));
        const data = await Utilities.getFetch<SubprocessUsageModel>(`api/${getFetchPath()}/subprocessUsage/${environmentName}/${agentGroupName}`);
        dispatch(receiveSubprocessUsageData(data));
        dispatch(fetchFinish(FetchId.extraProcessData));
    };
}

export const fetchLogLevelUsageData = (environmentName: string, agentGroupName: string): ThunkResult<void, State> => {
    return async (dispatch) => {
        dispatch(fetchStart(FetchId.logLevelData));
        const data = await Utilities.getFetch<LogLevelUsageData>(`api/${getFetchPath()}/logLevel/${environmentName}/${agentGroupName}`);
        dispatch(receiveLogLevelUsageData(data));
        dispatch(fetchFinish(FetchId.logLevelData));
    };
}

export const fetchLogRetentionUsageData = (environmentName: string, agentGroupName: string): ThunkResult<void, State> => {
    return async (dispatch) => {
        dispatch(fetchStart(FetchId.logRetentionData));
        const data = await Utilities.getFetch<LogRetentionUsageData>(`api/${getFetchPath()}/logLevelRetention/${environmentName}/${agentGroupName}`);
        dispatch(receiveLogRetentionUsageData(data));
        dispatch(fetchFinish(FetchId.logRetentionData));
    };
}

export function fetchIndividualProcesses(environmentName: string, agentGroupName: string, processGuids: string[]): ThunkResult<void, State> {
    return async (dispatch) => {
        dispatch(fetchStart(FetchId.processes));
        const data = await fetchProcessesByGuid(environmentName, agentGroupName, processGuids);
        dispatch(receiveProcesses({ processCount: data.length, processes: data }));
        dispatch(fetchFinish(FetchId.processes));
    };
}

export function fetchProcessVersionDetails(processGuids: string[]) {
    return Utilities.postJsonAndParseResponse<HashMap<ProcessVersionDetails[]>>(`api/processList/processVersions`, processGuids);
}

export function fetchProcessVersion(agentGroup: string, processGuid: string | undefined): Promise<ProcessVersion> {
    return Utilities.getFetch<ProcessVersion>(`api/processList/processVersion/${agentGroup}/${processGuid}`);
}

export function fetchAgentVersions(): Promise<AgentVersionInfo[]> {
    return Utilities.getFetch(`api/environment/agentVersions`);
}

function fetchAgentStatuses(environmentName: string, agentGroupName: string): Promise<AgentStatusPerAgentAndProcess> {
    return Utilities.getFetch(`api/processList/agentStatuses/${environmentName}/${agentGroupName}`);
}

export function fetchSuggestedProcessVariablesForDeployment(agentGroup: string, processIds: number[]): Promise<HashMap<IProcessVariable[]>> {
    return Utilities.getFetch<HashMap<IProcessVariable[]>>(`api/processList/suggestedProcessVariablesForDeployment/${agentGroup}`,
        {
            processIds: processIds.map((id) => id.toString()),
        });
}

export function fetchProcessesByGuid(environmentName: string, agentGroupName: string | null, processIds: string[]) {
    return Utilities.postJsonAndParseResponse<Process[]>(`api/${getFetchPath()}/${environmentName}/${agentGroupName}`, processIds);
}

export function fetchAvailableTags(): ThunkAction<void, State, null, Redux.Action> {
    return (dispatch) => {
        Utilities.getFetch<string[]>(`api/tag`)
            .then((tags) => dispatch(receiveAvailableTags(tags)))
            .catch((err) => dispatch(reportFetchError(err)));
    };
}

export function fetchEnvironmentVariables(): ThunkAction<void, State, null, Redux.Action> {
    return (dispatch) => {
        Utilities.getFetch<string[]>(`api/environmentVariable/keys`)
            .then((keys) => dispatch(receiveEnvironmentVariables(keys)))
            .catch((err) => dispatch(reportFetchError(err)));
    };
}

function fetchProcessCountData(environmentName: string, agentGroupName: string, countsTimeLimitDays: number) {
    return Utilities.getFetch<ProcessCountsResult>(`api/processList/instanceCounts/${environmentName}/${agentGroupName}?countsTimeLimitDays=${countsTimeLimitDays}`);
}

interface ProcessCountsResult {
    executionCounts: ProcessCountState;
}
export function shouldFetchSubprocessUsageData(filterCategory: FilterCategory | string) {
    const visibleColumns = getVisibleColumnsFromStore(AppPath.isSubprocessList());
    return visibleColumns.some((column) => column === "usedProcessesList" || column === "usedByProcessesList")
        || filterCategory === FilterCategory.Subprocesses
        || filterCategory === FilterCategory.UsedBy;
}

export const shouldFetchLogLevelUsageData = () => {
    const visibleColumns = getVisibleColumnsFromStore(AppPath.isSubprocessList());
    return visibleColumns.some((column) => column === "logLevel");
}

export const shouldFetchLogRetentionUsageData = () => {
    const visibleColumns = getVisibleColumnsFromStore(AppPath.isSubprocessList());
    return visibleColumns.some((column) => column === "logRetention");
}

export function fetchProcesses(): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const currentAgentGroup = getCurrentAgentGroup(state);
            dispatch(fetchStart(FetchId.processes));
            const processData = await fetchProcessData(
                currentAgentGroup.environment,
                currentAgentGroup.name,
                state);

            dispatch(receiveProcesses(processData));
            dispatch(fetchFinish(FetchId.processes));
        } catch (err) {
            dispatch(reportFetchError(err));
        }
    };
}


function fetchProcessesAndCounts(environmentName: string | null = null, agentGroupName: string | null = null): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            if (!environmentName || !agentGroupName) {
                const currentAgentGroup = getCurrentAgentGroup(state);
                environmentName = currentAgentGroup.environment;
                agentGroupName = currentAgentGroup.name;
            }
            const countsTimeLimitDays = state.listState.countsTimeLimitDays;
            // start fetches first, then await them, processes first, once everything has been started
            dispatch(fetchStart(FetchId.processes));
            const fetchProcessDataPromise = fetchProcessData(
                environmentName,
                agentGroupName, state);

            dispatch(fetchStart(FetchId.agentStatusesAndProcessCounts));
            const fetchAgentStatusesPromise = fetchAgentStatuses(environmentName, agentGroupName);
            const fetchProcessCountsPromise = fetchProcessCountData(environmentName, agentGroupName, countsTimeLimitDays);

            dispatch(fetchDrafts());

            const processData = await fetchProcessDataPromise;
            dispatch(receiveProcesses(processData));
            dispatch(fetchFinish(FetchId.processes));

            if (shouldFetchSubprocessUsageData(AppPath.getSearchParameters().filterCategory as FilterCategory)) {
                dispatch(fetchSubprocessUsageData(environmentName, agentGroupName));
            }

            if (shouldFetchLogLevelUsageData()) {
                dispatch(fetchLogLevelUsageData(environmentName, agentGroupName));
            }
    
            if (shouldFetchLogRetentionUsageData()) {
                dispatch(fetchLogRetentionUsageData(environmentName, agentGroupName));
            }

            const [agentStatuses, processCounts] = await Promise.all<any>([
                fetchAgentStatusesPromise,
                fetchProcessCountsPromise]);
            dispatch(receiveAgentStatuses(agentStatuses));
            dispatch(resetProcessCounts(processCounts.executionCounts));

            dispatch(fetchFinish(FetchId.agentStatusesAndProcessCounts));

        } catch (err) {
            dispatch(reportFetchError(err));
        }
    };
}

export const fetchProcessAndCountUpdate = fetchProcessesAndCounts;

export function fetchCounts(environmentName: string, agentGroupName: string, countsTimeLimitDays: number): ThunkResult<void, State> {
    return async (dispatch) => {
        try {
            // start fetches first, then await them, processes first, once everything has been started
            dispatch(fetchStart(FetchId.processCounts));
            const processCountsData = await fetchProcessCountData(environmentName, agentGroupName, countsTimeLimitDays);
            dispatch(resetProcessCounts(processCountsData.executionCounts));
            dispatch(fetchFinish(FetchId.processCounts));
        } catch (err) {
            dispatch(reportFetchError(err));
        }
    };
}

export const RECEIVE_PROCESSES = "RECEIVE_PROCESSES";
function receiveProcesses(data: ProcessListResponse): ReceiveProcessesAction {
    return {
        type: RECEIVE_PROCESSES,
        processes: data.processes,
        processCount: data.processCount
    };
}

export interface ReceiveProcessesAction extends Redux.Action {
    processes: Process[];
    processCount: number;
}

export const RECEIVE_DRAFTS = "RECEIVE_DRAFTS";
function receiveDrafts(data: Draft[]): ReceiveDraftsAction {
    return {
        type: RECEIVE_DRAFTS,
        drafts: data
    };
}

export interface ReceiveDraftsAction extends Redux.Action {
    drafts: Draft[];
}

export const RECEIVE_SUBPROCESSES_USAGE_DATA = "RECEIVE_SUBPROCESSES_USAGE_DATA";
export function receiveSubprocessUsageData(data: SubprocessUsageModel): ReceiveSubprocessUsageDataAction {
    return {
        type: RECEIVE_SUBPROCESSES_USAGE_DATA,
        data: data
    };
}

export interface ReceiveLogLevelUsageDataAction extends Redux.Action {
    data: LogLevelUsageData;
}

export const RECEIVE_LOGLEVEL_USAGE_DATA = "RECEIVE_LOGLEVEL_USAGE_DATA";
export function receiveLogLevelUsageData(data: LogLevelUsageData): ReceiveLogLevelUsageDataAction {
    return {
        type: RECEIVE_LOGLEVEL_USAGE_DATA,
        data: data
    };
}

export interface ReceiveLogRetentionDataAction extends Redux.Action {
    data: LogRetentionUsageData;
}

export const RECEIVE_LOGRETENTION_USAGE_DATA = "RECEIVE_LOGRETENTION_USAGE_DATA";
export function receiveLogRetentionUsageData(data: LogRetentionUsageData): ReceiveLogRetentionDataAction {
    return {
        type: RECEIVE_LOGRETENTION_USAGE_DATA,
        data: data
    };
}

export interface ReceiveSubprocessUsageDataAction extends Redux.Action {
    data: SubprocessUsageModel;
}

export const RECEIVE_AGENT_STATUSES = "RECEIVE_AGENT_STATUSES";
function receiveAgentStatuses(data: AgentStatusPerAgentAndProcess): ReceiveAgentStatusesAction {
    return {
        type: RECEIVE_AGENT_STATUSES,
        agentStatuses: data
    };
}

export interface ReceiveAgentStatusesAction extends Redux.Action {
    agentStatuses: AgentStatusPerAgentAndProcess;
}

export enum FetchId {
    processes = "processes",
    processCounts = "processCounts",
    agentStatusesAndProcessCounts = "agentStatusesAndProcessCounts",
    agentGroups = "agentGroups",
    activate = "activate",
    deactivate = "deactivate",
    delete = "delete",
    copy = "copy",
    rebuild = "rebuild",
    discardDraft = "discardDraft",
    tagProcesses = "tagProcesses",
    deploy = "deploy",
    import = "import",
    export = "export",
    runOnce = "runOnce",
    extraProcessData = "extraProcessData",
    tasks = "tasks",
    drafts = "drafts",
    setDebugExpires = "setDebugExpires",
    saveLogSettings = "saveLogSettings,",
    openLogSettings = "openLogSettings",
    pinProcesses = "pinProcesses",
    usedPromotedVariables = "promotedVariables",
    usedTriggers = "usedTriggers",
    usedTasks = "usedTasks",
    cleanUpVersions = "cleanUpVersions",
    logLevelData = "logLevelData",
    logRetentionData = "logRetentionData",
    all = "all"
}

export const FETCH_START = "FETCH_START";
export function fetchStart(fetchId: FetchId): FetchAction {
    return {
        type: FETCH_START,
        actionId: fetchId
    };
}

export const FETCH_FINISH = "FETCH_FINISH";
export function fetchFinish(fetchId: FetchId): FetchAction {
    return {
        type: FETCH_FINISH,
        actionId: fetchId
    };
}

export interface FetchAction extends Redux.Action {
    actionId: string;
}

export const FETCH_ERROR = "FETCH_ERROR";
export function fetchError(error: string): FetchErrorAction {
    return {
        type: FETCH_ERROR,
        error: error
    };
}

export interface FetchErrorAction extends Redux.Action {
    error: string;
}

export const RECEIVE_AGENT_GROUPS = "RECEIVE_AGENT_GROUPS";
export function receiveAgentGroups(data: HashMap<AgentGroup>): ReceiveAgentGroupsAction {
    return {
        type: RECEIVE_AGENT_GROUPS,
        agentGroups: data
    };
}

export interface ReceiveAgentGroupsAction extends Redux.Action {
    agentGroups: HashMap<AgentGroup>;
}

export const RECEIVE_AVAILABLE_TAGS = "RECEIVE_AVAILABLE_TAGS";
export interface ReceiveAvailableTagsAction extends Redux.Action {
    tags: string[];
}

function receiveAvailableTags(tags: string[]): ReceiveAvailableTagsAction {
    return {
        type: RECEIVE_AVAILABLE_TAGS,
        tags: tags
    };
}

export const RECEIVE_ENVIRONMENT_VARIABLES = "RECEIVE_ENVIRONMENT_VARIABLES";
export interface ReceiveEnvironmentVariablesAction extends Redux.Action {
    environmentVariables: string[];
}

function receiveEnvironmentVariables(environmentVariables: string[]): ReceiveEnvironmentVariablesAction {
    return {
        type: RECEIVE_ENVIRONMENT_VARIABLES,
        environmentVariables: environmentVariables
    };
}

export interface ReceiveAgentGroupsAction extends Redux.Action {
    agentGroups: HashMap<AgentGroup>;
}

export function reportFetchError(err: string | Error): ThunkResult<void, State> {
    return (dispatch) => {
        const errMsg = err.toString();
        // tslint:disable-next-line:no-console
        console.error(err);
        Utilities.toastError(errMsg);
        dispatch(fetchError(errMsg));
    };
}


